import { LICENSE_CONFIG } from './constants.js'
import { getBatchAvailable } from './portalSeatExpiry.js'

export const VALID_SEAT_LICENSE_TYPES = Object.keys(LICENSE_CONFIG)

/** @param {string | undefined | null} type */
export function resolveSeatLicenseType(type) {
  if (type && VALID_SEAT_LICENSE_TYPES.includes(type)) return type
  return 'Basic'
}

/**
 * Plan downgrades convert every in-progress seat — no per-course opt-out in the portal.
 * @param {string | undefined | null} from
 * @param {string | undefined | null} to
 */
export function isLicenseConversionDowngrade(from, to) {
  const pair = `${from ?? ''}→${to ?? ''}`
  return pair === 'Premium→Basic' || pair === 'Supp. Premium→Supp. Basic'
}

/** Course Premium or Supplemental Pack Premium. */
export function isPremiumSeatLicenseType(type) {
  const resolved = resolveSeatLicenseType(type)
  return resolved === 'Premium' || resolved === 'Supp. Premium'
}

/**
 * Conversions that apply to every in-progress seat — no per-learner opt-out in the portal.
 * Includes plan downgrades and no-fee Legacy → Basic.
 */
export function conversionIncludesAllInProgressSeats(from, to) {
  if (isLicenseConversionDowngrade(from, to)) return true
  const pair = `${from ?? ''}→${to ?? ''}`
  return pair === 'Legacy→Basic' || pair === 'Supp. Legacy→Supp. Basic'
}

/** Course Legacy or supplemental pack Legacy. */
export function isLegacySeatLicenseType(type) {
  const resolved = resolveSeatLicenseType(type)
  return resolved === 'Legacy' || resolved === 'Supp. Legacy'
}

/**
 * Basic license that replaces a Legacy plan when buying more seats.
 * Returns null when `type` is not a Legacy license.
 * @param {string | undefined | null} type
 */
export function basicSeatLicenseReplacingLegacy(type) {
  const resolved = resolveSeatLicenseType(type)
  if (resolved === 'Supp. Legacy') return 'Supp. Basic'
  if (resolved === 'Legacy') return 'Basic'
  return null
}

/** @param {Record<string, unknown> | null | undefined} batch @param {number} index */
export function normalizeBatch(batch, index = 0) {
  if (!batch || typeof batch !== 'object') return null
  const quantity = Number(batch.quantity) || 0
  const used = Number(batch.used ?? batch.consumed ?? 0)
  const type = resolveSeatLicenseType(
    /** @type {string} */ (batch.type || batch.licenseType)
  )
  const purchaseDate =
    batch.purchaseDate ||
    batch.purchasedAt ||
    (batch.createdAt ? String(batch.createdAt).slice(0, 10) : null) ||
    new Date().toISOString().slice(0, 10)

  return {
    id: batch.id ?? batch._id ?? `batch-${index}`,
    quantity,
    used,
    purchaseDate,
    type,
    licenseType: batch.licenseType ?? batch.type ?? type,
    invoiceNum: batch.invoiceNum ?? batch.invoiceNumber ?? null,
    orderId: batch.orderId ?? null,
    billingDocumentId: batch.billingDocumentId ?? null,
    expiresAt: batch.expiresAt ?? null,
    expiredQuantity: Number(batch.expiredQuantity) || 0,
    status: batch.status ?? 'active',
    sourceType: batch.sourceType ?? null,
    lineItemKey: batch.lineItemKey ?? null,
  }
}

/** @param {unknown} batches */
export function normalizeBatches(batches) {
  if (!Array.isArray(batches)) return []
  return batches.map((b, i) => normalizeBatch(b, i)).filter(Boolean)
}

/** @param {{ quantity?: number }[]} batches */
export function sumBatchQuantities(batches) {
  return (batches || []).reduce((sum, b) => sum + (Number(b.quantity) || 0), 0)
}

/** @param {Array<Record<string, unknown>>} batches */
export function sumBatchRemaining(batches) {
  return (batches || []).reduce((sum, b) => sum + getBatchAvailable(b), 0)
}

/** Portal batch shape → store/API payload */
export function batchToStore(batch) {
  const used = Number(batch.used) || 0
  return {
    id: batch.id,
    quantity: Number(batch.quantity) || 0,
    used,
    consumed: used,
    purchaseDate: batch.purchaseDate,
    type: batch.type,
    licenseType: batch.licenseType ?? batch.type,
    invoiceNum: batch.invoiceNum ?? undefined,
    orderId: batch.orderId ?? undefined,
    billingDocumentId: batch.billingDocumentId ?? undefined,
    expiresAt: batch.expiresAt ?? undefined,
    expiredQuantity: batch.expiredQuantity ?? undefined,
    status: batch.status ?? undefined,
    sourceType: batch.sourceType ?? undefined,
    lineItemKey: batch.lineItemKey ?? undefined,
  }
}

/** Org field for current seat/course license. */
export function organizationSeatLicenseType(org) {
  if (!org) return ''
  return org.seatLicenseType || ''
}

/** Supplemental pack orgs — not course seat licenses. */
export function isSupplementalSeatLicenseType(type) {
  const resolved = resolveSeatLicenseType(type)
  return LICENSE_CONFIG[resolved]?.category === 'supplemental'
}

/** Publications-only seat license (NFHS title distribution). */
export function isPublicationsSeatLicenseType(type) {
  const resolved = resolveSeatLicenseType(type)
  return LICENSE_CONFIG[resolved]?.category === 'publications'
}

/** @param {{ seatLicenseType?: string } | null | undefined} org */
export function organizationIsSupplemental(org) {
  return isSupplementalSeatLicenseType(organizationSeatLicenseType(org))
}

/** @param {{ seatLicenseType?: string } | null | undefined} org */
export function organizationHasPublicationsSeatLicense(org) {
  return isPublicationsSeatLicenseType(organizationSeatLicenseType(org))
}

/**
 * Seat type for portal UI. If org is classified publications (portalType/type/types)
 * but still has a leftover course seatLicenseType, treat as Publications.
 * @param {Record<string, unknown> | null | undefined} org
 * @param {(org: unknown) => boolean} [isPublicationsOrg]
 */
export function resolveEffectiveSeatLicenseType(org, isPublicationsOrg) {
  const raw = organizationSeatLicenseType(org)
  const resolved = resolveSeatLicenseType(raw)
  const publications =
    typeof isPublicationsOrg === 'function'
      ? isPublicationsOrg(org)
      : isPublicationsSeatLicenseType(resolved) ||
        String(org?.portalType || '').trim().toLowerCase() === 'publications' ||
        String(org?.type || '').trim().toLowerCase() === 'publications'

  if (publications && !isSupplementalSeatLicenseType(resolved) && resolved !== 'Publications') {
    return 'Publications'
  }
  return resolved
}

/** Default bill-to fields — see portalBillingForm.js */
export { buildDocFieldsFromOrganization } from './portalBillingForm.js'

/** @param {{ seatLicenseType?: string, seatPurchaseBatches?: unknown, seats?: number }} | null | undefined} org */
export function readOrganizationLicensing(org) {
  const seatLicenseType = resolveSeatLicenseType(organizationSeatLicenseType(org))
  const seatPurchaseBatches = normalizeBatches(org?.seatPurchaseBatches)
  const remainingSeats = Number(org?.seats ?? sumBatchRemaining(seatPurchaseBatches)) || 0
  return { seatLicenseType, seatPurchaseBatches, remainingSeats }
}

/** @param {{ seatLicenseType?: string, seatPurchaseBatches?: unknown, seats?: number }} org @param {{ seatLicenseType: string, seatPurchaseBatches: unknown[], remainingSeats: number }} licensing */
export function writeOrganizationLicensing(org, licensing) {
  if (!org) return
  org.seatLicenseType = licensing.seatLicenseType
  org.seatPurchaseBatches = licensing.seatPurchaseBatches.map(batchToStore)
  org.seats = licensing.remainingSeats
}

/**
 * Apply licensing block from POST /fulfill-license-conversion (or paid fulfillment).
 * @param {Record<string, unknown> | null | undefined} org
 * @param {Record<string, unknown> | null | undefined} licensing
 */
export function applyApiLicensingToOrganization(org, licensing) {
  if (!org || !licensing || typeof licensing !== 'object') return

  if (licensing.seatLicenseType != null) {
    org.seatLicenseType = licensing.seatLicenseType
  }
  if (licensing.seats != null) {
    org.seats = Math.max(0, Number(licensing.seats) || 0)
  }
  if (licensing.cumulativeSeats != null) {
    org.cumulativeSeats = Number(licensing.cumulativeSeats) || 0
  }
  if (Array.isArray(licensing.seatPurchaseBatches)) {
    org.seatPurchaseBatches = licensing.seatPurchaseBatches
  }
}

/**
 * Licensing payload from assign/unassign (and similar) API responses.
 * Supports `{ licensing }`, `{ organization: { seats, seatPurchaseBatches } }`, or top-level fields.
 * @param {Record<string, unknown> | null | undefined} data
 */
export function resolveLicensingFromApiResponse(data) {
  if (!data || typeof data !== 'object') return null
  if (data.licensing && typeof data.licensing === 'object') {
    return /** @type {Record<string, unknown>} */ (data.licensing)
  }
  const org = data.organization
  if (org && typeof org === 'object') {
    const o = /** @type {Record<string, unknown>} */ (org)
    if (o.seats != null || Array.isArray(o.seatPurchaseBatches)) {
      return {
        seats: o.seats,
        seatLicenseType: o.seatLicenseType,
        seatPurchaseBatches: o.seatPurchaseBatches,
        cumulativeSeats: o.cumulativeSeats,
      }
    }
  }
  if (data.seats != null || Array.isArray(data.seatPurchaseBatches)) {
    return /** @type {Record<string, unknown>} */ (data)
  }
  return null
}

/**
 * Apply seats + batches from an API response onto the active org (and optional org list).
 * @returns {boolean} true when response included licensing to apply
 */
export function applyOrganizationLicensingFromApiResponse(org, data, organizationsList = null) {
  const licensing = resolveLicensingFromApiResponse(data)
  if (!licensing || !org) return false
  applyApiLicensingToOrganization(org, licensing)
  if (Array.isArray(organizationsList) && org.companyID) {
    const match = organizationsList.find((o) => o?.companyID === org.companyID)
    if (match) applyApiLicensingToOrganization(match, licensing)
  }
  return true
}

function formatPortalDate(value) {
  if (!value) return null
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return typeof value === 'string' ? value : null
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/** Map API/store instructor → portal modal row shape */
export function normalizeInstructorForPortal(raw, index = 0) {
  if (!raw || typeof raw !== 'object') return null
  const name =
    raw.name ||
    [raw.firstName, raw.lastName].filter(Boolean).join(' ').trim() ||
    'Unknown'
  let roles = []
  if (Array.isArray(raw.roles)) roles = raw.roles.filter(Boolean)
  else if (raw.roles) roles = [raw.roles]
  else if (Array.isArray(raw.role)) roles = raw.role.filter(Boolean)
  else if (raw.role) roles = [raw.role]
  else if (raw.type) roles = [raw.type]
  else roles = ['Instructor']

  return {
    id: raw.id ?? raw.userId ?? raw._id ?? `instructor-${index}`,
    name,
    email: raw.email || '',
    roles,
    activationDate: formatPortalDate(raw.activationDate || raw.dateAdded || raw.createdAt) || '—',
    emailOpened: Boolean(raw.emailOpened),
    emailSentDate: raw.emailSentDate ? formatPortalDate(raw.emailSentDate) || raw.emailSentDate : null,
    emailOpenedDate: raw.emailOpenedDate
      ? formatPortalDate(raw.emailOpenedDate) || raw.emailOpenedDate
      : null,
  }
}

/** @param {unknown} instructors */
export function normalizeInstructorsForPortal(instructors) {
  if (!Array.isArray(instructors)) return []
  return instructors.map((row, i) => normalizeInstructorForPortal(row, i)).filter(Boolean)
}
